import { useSearchParams } from 'react-router-dom';

import { filterDefined } from '@/utils/arrayUtils';

export function useUrlFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Updates a filter in the URL based on selected keys and their corresponding content values.
   *
   * @param key - The query parameter key (e.g., 'serviceDomains')
   * @param selectedKeys - The selected keys (e.g., ['domain1', 'domain2'])
   * @param options - The available options with selectedKey and content
   */
  const updateFilterInURL = (
    key: string,
    selectedKeys: string[],
    options: { selectedKey: string; content: string[] }[],
  ) => {
    const values = selectedKeys.map((k) => options.find((opt) => opt.selectedKey === k)?.content[0]).filter(Boolean);

    const newValues = [...new Set(values)];

    const queryParams = new URLSearchParams(searchParams);
    if (newValues.length > 0) {
      queryParams.set(key, newValues.join(','));
    } else {
      queryParams.delete(key);
    }

    setSearchParams(queryParams);
  };

  /**
   * Retrieves selected keys from the URL based on a query parameter key.
   *
   * @param key - The query parameter key (e.g., 'serviceDomains')
   * @param options - The available options with selectedKey and content
   * @returns An array of selected keys
   */
  /**
   * Retrieves selected keys from the URL based on a query parameter key.
   *
   * @param key - The query parameter key (e.g., 'serviceDomains')
   * @param options - The available options with selectedKey and content
   * @returns An array of selected keys or undefined values
   */
  const getSelectedKeysFromURL = (key: string, options: { selectedKey: string; content: string[] }[]): string[] => {
    const raw = searchParams.get(key);
    if (!raw) return [];

    const values = raw.split(',').filter(Boolean);

    return filterDefined(values.map((v) => options.find((opt) => opt.content[0] === v)?.selectedKey));
  };

  /**
   * Clears all known filter keys from the URL.
   */

  const clearAllFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  return {
    updateFilterInURL,
    getSelectedKeysFromURL,
    clearAllFilters,
  };
}
